import type { KeyboardEvent, PointerEvent, ReactNode } from 'react'
import type { AxisTick } from '@/components/charts/scales'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { useElementWidth } from '@/hooks/use-element-width'
import { niceDomain, scaleLinear } from '@/components/charts/scales'

type Values = Array<number | null>

export type ChartSeries =
    | { kind: 'line', id: string, label: string, color: string, values: Values, width?: number, dashed?: boolean, format?: (value: number) => string }
    | { kind: 'area', id: string, label: string, color: string, values: Values, format?: (value: number) => string }
    | { kind: 'candles', id: string, label: string, open: number[], high: number[], low: number[], close: number[], format?: (value: number) => string }
    | { kind: 'bars', id: string, label: string, values: Values, color: string | ((index: number, value: number) => string), format?: (value: number) => string }
    | { kind: 'band', id: string, label: string, color: string, upper: Values, lower: Values, format?: (value: number) => string }

export interface ChartGuide {
    value: number
    label?: string
}

export interface ChartPane {
    id: string
    height: number
    series: ChartSeries[]
    format: (value: number) => string
    domain?: [number, number]
    zero?: boolean
    guides?: ChartGuide[]
    label?: string
    tickCount?: number
    marker?: { value: number, tone: 'up' | 'down' | 'flat' } | null
}

interface TimeChartProps {
    count: number
    labels: string[]
    xTicks: (plotWidth: number) => AxisTick[]
    panes: ChartPane[]
    ariaLabel: string
    className?: string
    defaultWidth?: number
}

interface PaneGeometry {
    pane: ChartPane
    top: number
    bottom: number
    min: number
    max: number
    ticks: number[]
    y: (value: number) => number
}

interface HoverState {
    index: number
    y: number | null
}

const LEFT = 4
const AXIS = 60
const X_BAND = 24
const GAP = 14
const TOP = 8

const isValue = (value: number | null | undefined): value is number => typeof value === 'number' && Number.isFinite(value)

const fmt = (value: number): string => value.toFixed(1)

const linePath = (values: Values, x: (index: number) => number, y: (value: number) => number): string => {
    let path = ''
    let open = false

    for (let index = 0; index < values.length; index++) {
        const value = values[index]

        if (!isValue(value)) {
            open = false
            continue
        }

        path += `${open ? 'L' : 'M'}${fmt(x(index))},${fmt(y(value))}`
        open = true
    }

    return path
}

const areaPath = (values: Values, x: (index: number) => number, y: (value: number) => number, base: number): string => {
    let path = ''
    let segmentStart = -1

    const close = (end: number) => {
        if (segmentStart < 0) return
        path += `L${fmt(x(end))},${fmt(base)}L${fmt(x(segmentStart))},${fmt(base)}Z`
        segmentStart = -1
    }

    for (let index = 0; index < values.length; index++) {
        const value = values[index]

        if (!isValue(value)) {
            close(index - 1)
            continue
        }

        if (segmentStart < 0) {
            segmentStart = index
            path += `M${fmt(x(index))},${fmt(y(value))}`
        }
        else {
            path += `L${fmt(x(index))},${fmt(y(value))}`
        }
    }

    close(values.length - 1)

    return path
}

const bandPath = (upper: Values, lower: Values, x: (index: number) => number, y: (value: number) => number): string => {
    let path = ''
    let segment: number[] = []

    const flush = () => {
        if (segment.length > 1) {
            path += segment.map((index, position) => `${position === 0 ? 'M' : 'L'}${fmt(x(index))},${fmt(y(upper[index] as number))}`).join('')
            path += [...segment].reverse().map(index => `L${fmt(x(index))},${fmt(y(lower[index] as number))}`).join('')
            path += 'Z'
        }

        segment = []
    }

    for (let index = 0; index < upper.length; index++) {
        if (isValue(upper[index]) && isValue(lower[index])) segment.push(index)
        else flush()
    }

    flush()

    return path
}

const barPath = (left: number, width: number, base: number, value: number, radius: number): string => {
    const top = Math.min(base, value)
    const bottom = Math.max(base, value)
    const height = Math.max(0.6, bottom - top)
    const r = Math.min(radius, width / 2, height)

    if (r < 1) return `M${fmt(left)},${fmt(top)}h${fmt(width)}v${fmt(height)}h${fmt(-width)}Z`

    if (value <= base) {
        return `M${fmt(left)},${fmt(bottom)}V${fmt(top + r)}Q${fmt(left)},${fmt(top)} ${fmt(left + r)},${fmt(top)}H${fmt(left + width - r)}Q${fmt(left + width)},${fmt(top)} ${fmt(left + width)},${fmt(top + r)}V${fmt(bottom)}Z`
    }

    return `M${fmt(left)},${fmt(top)}V${fmt(bottom - r)}Q${fmt(left)},${fmt(bottom)} ${fmt(left + r)},${fmt(bottom)}H${fmt(left + width - r)}Q${fmt(left + width)},${fmt(bottom)} ${fmt(left + width)},${fmt(bottom - r)}V${fmt(top)}Z`
}

const extentOf = (pane: ChartPane): [number, number, boolean] => {
    let min = Infinity
    let max = -Infinity
    let hasBars = false

    const consider = (value: number | null | undefined) => {
        if (!isValue(value)) return
        if (value < min) min = value
        if (value > max) max = value
    }

    for (const series of pane.series) {
        if (series.kind === 'candles') {
            series.low.forEach(consider)
            series.high.forEach(consider)
        }
        else if (series.kind === 'band') {
            series.upper.forEach(consider)
            series.lower.forEach(consider)
        }
        else {
            series.values.forEach(consider)
            if (series.kind === 'bars') hasBars = true
        }
    }

    for (const guide of pane.guides ?? []) consider(guide.value)

    if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1, hasBars]

    return [min, max, hasBars]
}

const valueAt = (series: ChartSeries, index: number): number | null => {
    if (series.kind === 'candles') return series.close[index] ?? null
    if (series.kind === 'band') return series.upper[index] ?? null

    return series.values[index] ?? null
}

const colorOf = (series: ChartSeries, index: number): string => {
    if (series.kind === 'candles') return (series.close[index] ?? 0) >= (series.open[index] ?? 0) ? 'var(--up)' : 'var(--down)'
    if (series.kind === 'bars') {
        const value = series.values[index]

        return typeof series.color === 'function' ? series.color(index, isValue(value) ? value : 0) : series.color
    }

    return series.color
}

const TimeChart = ({ count, labels, xTicks, panes, ariaLabel, className, defaultWidth = 760 }: TimeChartProps): ReactNode => {
    const [ref, width] = useElementWidth<HTMLDivElement>(defaultWidth)
    const [hover, setHover] = useState<HoverState | null>(null)

    const plotWidth = Math.max(40, width - LEFT - AXIS)
    const plotRight = LEFT + plotWidth
    const step = plotWidth / Math.max(1, count)
    const x = (index: number): number => LEFT + (index + 0.5) * step
    const totalHeight = TOP + panes.reduce((acc, pane) => acc + pane.height, 0) + GAP * Math.max(0, panes.length - 1) + X_BAND

    const geometry: PaneGeometry[] = useMemo(() => {
        let cursor = TOP

        return panes.map(pane => {
            const [rawMin, rawMax, hasBars] = extentOf(pane)
            const domain = pane.domain
                ? { min: pane.domain[0], max: pane.domain[1], ticks: niceDomain(pane.domain[0], pane.domain[1], pane.tickCount ?? 4, 0).ticks }
                : niceDomain(rawMin, rawMax, pane.tickCount ?? 4, 0.08, Boolean(pane.zero) || hasBars)
            const top = cursor
            const bottom = cursor + pane.height
            cursor = bottom + GAP

            return {
                pane,
                top,
                bottom,
                min: domain.min,
                max: domain.max,
                ticks: domain.ticks.filter(tick => tick >= domain.min && tick <= domain.max),
                y: scaleLinear([domain.min, domain.max], [bottom, top])
            }
        })
    }, [panes])

    const ticks = useMemo(() => xTicks(plotWidth), [xTicks, plotWidth])

    const staticLayer = useMemo(() => {
        const position = (index: number): number => LEFT + (index + 0.5) * step

        return geometry.map(({ pane, top, bottom, ticks: yTicks, y, min }) => {
            const zeroY = min <= 0 ? y(0) : y(min)

            return (
                <g key={pane.id}>
                    {yTicks.map(tick => (
                        <g key={`grid-${pane.id}-${tick}`}>
                            <line x1={LEFT} x2={plotRight} y1={y(tick)} y2={y(tick)} stroke='var(--grid)' strokeWidth={1} shapeRendering='crispEdges' />
                            <text x={plotRight + 8} y={y(tick)} dy='0.32em' fontSize={11} fill='var(--muted-foreground)' className='tabular-nums'>
                                {pane.format(tick)}
                            </text>
                        </g>
                    ))}

                    <line x1={plotRight} x2={plotRight} y1={top} y2={bottom} stroke='var(--axis)' strokeWidth={1} shapeRendering='crispEdges' />

                    {(pane.guides ?? []).map(guide => (
                        <g key={`guide-${pane.id}-${guide.value}`}>
                            <line x1={LEFT} x2={plotRight} y1={y(guide.value)} y2={y(guide.value)} stroke='var(--muted-foreground)' strokeOpacity={0.55} strokeWidth={1} strokeDasharray='3 4' />
                            {guide.label && (
                                <text x={LEFT + 6} y={y(guide.value) - 5} fontSize={10.5} fill='var(--muted-foreground)'>
                                    {guide.label}
                                </text>
                            )}
                        </g>
                    ))}

                    {pane.series.map(series => {
                        if (series.kind === 'band') {
                            return (
                                <g key={series.id}>
                                    <path d={bandPath(series.upper, series.lower, position, y)} fill={series.color} fillOpacity={0.08} />
                                    <path d={linePath(series.upper, position, y)} fill='none' stroke={series.color} strokeOpacity={0.55} strokeWidth={1} />
                                    <path d={linePath(series.lower, position, y)} fill='none' stroke={series.color} strokeOpacity={0.55} strokeWidth={1} />
                                </g>
                            )
                        }

                        if (series.kind === 'area') {
                            return (
                                <g key={series.id}>
                                    <path d={areaPath(series.values, position, y, min < 0 ? zeroY : bottom)} fill={series.color} fillOpacity={0.1} />
                                    <path d={linePath(series.values, position, y)} fill='none' stroke={series.color} strokeWidth={2} strokeLinejoin='round' strokeLinecap='round' />
                                </g>
                            )
                        }

                        if (series.kind === 'line') {
                            return (
                                <path
                                    key={series.id}
                                    d={linePath(series.values, position, y)}
                                    fill='none'
                                    stroke={series.color}
                                    strokeWidth={series.width ?? 2}
                                    strokeDasharray={series.dashed ? '4 4' : undefined}
                                    strokeLinejoin='round'
                                    strokeLinecap='round'
                                />
                            )
                        }

                        if (series.kind === 'candles') {
                            const bodyWidth = Math.max(1, Math.min(12, step * 0.62))
                            let upWicks = ''
                            let downWicks = ''
                            let upBodies = ''
                            let downBodies = ''

                            for (let index = 0; index < series.close.length; index++) {
                                const open = series.open[index]
                                const close = series.close[index]
                                const high = series.high[index]
                                const low = series.low[index]
                                if (!isValue(open) || !isValue(close) || !isValue(high) || !isValue(low)) continue

                                const cx = position(index)
                                const yTop = y(Math.max(open, close))
                                const height = Math.max(1, Math.abs(y(open) - y(close)))
                                const wick = `M${fmt(cx)},${fmt(y(high))}V${fmt(y(low))}`
                                const body = `M${fmt(cx - bodyWidth / 2)},${fmt(yTop)}h${fmt(bodyWidth)}v${fmt(height)}h${fmt(-bodyWidth)}Z`

                                if (close >= open) {
                                    upWicks += wick
                                    upBodies += body
                                }
                                else {
                                    downWicks += wick
                                    downBodies += body
                                }
                            }

                            return (
                                <g key={series.id}>
                                    <path d={upWicks} stroke='var(--up)' strokeWidth={1} fill='none' />
                                    <path d={downWicks} stroke='var(--down)' strokeWidth={1} fill='none' />
                                    <path d={upBodies} fill='var(--up)' />
                                    <path d={downBodies} fill='var(--down)' />
                                </g>
                            )
                        }

                        const barWidth = Math.max(1, Math.min(24, step * 0.7))
                        const radius = barWidth >= 6 ? 4 : 0
                        const groups = new Map<string, string>()

                        series.values.forEach((value, index) => {
                            if (!isValue(value)) return

                            const color = typeof series.color === 'function' ? series.color(index, value) : series.color
                            const path = barPath(position(index) - barWidth / 2, barWidth, zeroY, y(value), radius)
                            groups.set(color, (groups.get(color) ?? '') + path)
                        })

                        return (
                            <g key={series.id}>
                                {Array.from(groups.entries()).map(([color, path]) => (
                                    <path key={color} d={path} fill={color} />
                                ))}
                            </g>
                        )
                    })}

                    {pane.label && (
                        <text x={LEFT + 6} y={top + 13} fontSize={11} fontWeight={500} fill='var(--muted-foreground)'>
                            {pane.label}
                        </text>
                    )}

                    {pane.marker && isValue(pane.marker.value) && pane.marker.value >= min && (
                        <g transform={`translate(${plotRight + 2}, ${y(pane.marker.value)})`}>
                            <rect
                                x={0}
                                y={-9}
                                width={AXIS - 4}
                                height={18}
                                rx={4}
                                fill={pane.marker.tone === 'up' ? 'var(--up)' : pane.marker.tone === 'down' ? 'var(--down)' : 'var(--flat)'}
                            />
                            <text x={6} y={0} dy='0.34em' fontSize={11} fontWeight={600} fill='#0a0f14' className='tabular-nums'>
                                {pane.format(pane.marker.value)}
                            </text>
                        </g>
                    )}
                </g>
            )
        })
    }, [geometry, plotRight, step])

    const xAxis = useMemo(() => {
        const baseline = totalHeight - X_BAND

        return (
            <g>
                {ticks.map(tick => {
                    const px = LEFT + (tick.index + 0.5) * step
                    const anchor = px < LEFT + 24 ? 'start' : px > plotRight - 24 ? 'end' : 'middle'

                    return (
                        <g key={`x-${tick.index}`}>
                            <line x1={px} x2={px} y1={baseline - 2} y2={baseline + 3} stroke='var(--axis)' strokeWidth={1} shapeRendering='crispEdges' />
                            <text x={px} y={baseline + 16} fontSize={11} textAnchor={anchor} fill='var(--muted-foreground)'>
                                {tick.label}
                            </text>
                        </g>
                    )
                })}
            </g>
        )
    }, [ticks, step, totalHeight, plotRight])

    const updateFromPointer = (event: PointerEvent<SVGRectElement>) => {
        const svg = event.currentTarget.ownerSVGElement
        if (!svg || count === 0) return

        const bounds = svg.getBoundingClientRect()
        const scaleX = width / Math.max(1, bounds.width)
        const scaleY = totalHeight / Math.max(1, bounds.height)
        const px = (event.clientX - bounds.left) * scaleX
        const py = (event.clientY - bounds.top) * scaleY
        const index = Math.min(count - 1, Math.max(0, Math.floor((px - LEFT) / step)))

        setHover({ index, y: py })
    }

    const handleKey = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!count) return

        const current = hover?.index ?? count - 1
        let next = current

        if (event.key === 'ArrowLeft') next = Math.max(0, current - 1)
        else if (event.key === 'ArrowRight') next = Math.min(count - 1, current + 1)
        else if (event.key === 'Home') next = 0
        else if (event.key === 'End') next = count - 1
        else if (event.key === 'Escape') {
            setHover(null)

            return
        }
        else return

        event.preventDefault()
        setHover({ index: next, y: null })
    }

    const active = hover ? Math.min(count - 1, hover.index) : null
    const activeX = active !== null ? x(active) : 0
    const hoveredPane = hover && hover.y !== null ? geometry.find(item => hover.y !== null && hover.y >= item.top && hover.y <= item.bottom) ?? null : null
    const tooltipOnLeft = active !== null && activeX > LEFT + plotWidth * 0.58

    return (
        <div
            ref={ref}
            role='group'
            aria-label={ariaLabel}
            tabIndex={0}
            onKeyDown={handleKey}
            onBlur={() => setHover(null)}
            className={cn('relative w-full select-none outline-none focus-visible:ring-2 focus-visible:ring-ring/60 rounded-md', className)}
        >
            <svg
                width='100%'
                height={totalHeight}
                viewBox={`0 0 ${width} ${totalHeight}`}
                preserveAspectRatio='xMinYMin meet'
                aria-hidden='true'
                className='block overflow-visible'
            >
                {staticLayer}
                {xAxis}

                {active !== null && (
                    <g pointerEvents='none'>
                        <line
                            x1={activeX}
                            x2={activeX}
                            y1={TOP}
                            y2={totalHeight - X_BAND}
                            stroke='var(--foreground)'
                            strokeOpacity={0.35}
                            strokeWidth={1}
                            shapeRendering='crispEdges'
                        />

                        {geometry.map(({ pane, y, min, max }) => pane.series.map(series => {
                            if (series.kind !== 'line' && series.kind !== 'area') return null

                            const value = series.values[active]
                            if (!isValue(value) || value < min || value > max) return null

                            return (
                                <circle
                                    key={`dot-${pane.id}-${series.id}`}
                                    cx={activeX}
                                    cy={y(value)}
                                    r={4}
                                    fill={series.color}
                                    stroke='var(--surface)'
                                    strokeWidth={2}
                                />
                            )
                        }))}

                        {hoveredPane && hover && hover.y !== null && (
                            <g>
                                <line
                                    x1={LEFT}
                                    x2={plotRight}
                                    y1={hover.y}
                                    y2={hover.y}
                                    stroke='var(--foreground)'
                                    strokeOpacity={0.25}
                                    strokeWidth={1}
                                    shapeRendering='crispEdges'
                                />
                                <rect x={plotRight + 2} y={hover.y - 9} width={AXIS - 4} height={18} rx={4} fill='var(--secondary)' stroke='var(--axis)' />
                                <text x={plotRight + 8} y={hover.y} dy='0.34em' fontSize={11} fill='var(--foreground)' className='tabular-nums'>
                                    {hoveredPane.pane.format(hoveredPane.min + ((hoveredPane.bottom - hover.y) / (hoveredPane.bottom - hoveredPane.top)) * (hoveredPane.max - hoveredPane.min))}
                                </text>
                            </g>
                        )}
                    </g>
                )}

                <rect
                    x={LEFT}
                    y={0}
                    width={plotWidth}
                    height={totalHeight - X_BAND}
                    fill='transparent'
                    onPointerMove={updateFromPointer}
                    onPointerDown={updateFromPointer}
                    onPointerLeave={() => setHover(null)}
                    style={{ touchAction: 'pan-y' }}
                />
            </svg>

            {active !== null && (
                <div
                    className='pointer-events-none absolute top-2 z-10 min-w-44 max-w-64 rounded-lg border border-border bg-popover/95 px-3 py-2 text-xs shadow-xl backdrop-blur-sm'
                    style={tooltipOnLeft
                        ? { right: `${((width - activeX + 12) / width) * 100}%` }
                        : { left: `${((activeX + 12) / width) * 100}%` }}
                >
                    <p className='mb-1.5 font-medium text-muted-foreground'>
                        {labels[active] ?? ''}
                    </p>

                    <div className='space-y-1'>
                        {geometry.map(({ pane }) => pane.series.map(series => {
                            if (series.kind === 'candles') {
                                const rows: Array<[string, number]> = [
                                    ['Open', series.open[active]],
                                    ['High', series.high[active]],
                                    ['Low', series.low[active]],
                                    ['Close', series.close[active]]
                                ]
                                const format = series.format ?? pane.format

                                return (
                                    <div key={`tip-${pane.id}-${series.id}`} className='grid grid-cols-2 gap-x-3 gap-y-0.5'>
                                        {rows.map(([name, value]) => (
                                            <div key={name} className='flex items-baseline justify-between gap-2'>
                                                <span className='text-muted-foreground'>{name}</span>
                                                <span className='font-medium tabular-nums text-foreground'>{isValue(value) ? format(value) : '-'}</span>
                                            </div>
                                        ))}
                                    </div>
                                )
                            }

                            if (series.kind === 'band') {
                                const upper = series.upper[active]
                                const lower = series.lower[active]
                                if (!isValue(upper) || !isValue(lower)) return null
                                const format = series.format ?? pane.format

                                return (
                                    <div key={`tip-${pane.id}-${series.id}`} className='flex items-center justify-between gap-3'>
                                        <span className='flex items-center gap-1.5 text-muted-foreground'>
                                            <span className='inline-block h-0.5 w-3 rounded-full' style={{ background: series.color }} />
                                            {series.label}
                                        </span>
                                        <span className='font-medium tabular-nums text-foreground'>{format(lower)} to {format(upper)}</span>
                                    </div>
                                )
                            }

                            const value = valueAt(series, active)
                            if (!isValue(value)) return null
                            const format = series.format ?? pane.format

                            return (
                                <div key={`tip-${pane.id}-${series.id}`} className='flex items-center justify-between gap-3'>
                                    <span className='flex items-center gap-1.5 text-muted-foreground'>
                                        <span className='inline-block h-0.5 w-3 rounded-full' style={{ background: colorOf(series, active) }} />
                                        {series.label}
                                    </span>
                                    <span className='font-medium tabular-nums text-foreground'>{format(value)}</span>
                                </div>
                            )
                        }))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default TimeChart
